import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumb, Spinner } from '../../../shared/components';
import { useFieldOperationsSettings } from '../context/FieldOperationsSettingsContext';
import { updateFieldOperationsSettings } from '../services/fieldOperationsService';
import type { FieldOperationsSettings } from '../types/FieldOperations';
import styles from './FieldOperationsSettingsPage.module.css';

type TargetFlags = Pick<
  FieldOperationsSettings,
  'allowLocation' | 'allowAdHoc' | 'allowCalendarEvent' | 'allowTask'
>;

/** UI-only shortcuts: each one just flips the target switches below. */
const PRESETS: Array<{ id: string; targets: TargetFlags }> = [
  { id: 'multiSite', targets: { allowLocation: true, allowAdHoc: false, allowCalendarEvent: false, allowTask: false } },
  { id: 'homeVisits', targets: { allowLocation: true, allowAdHoc: false, allowCalendarEvent: true, allowTask: false } },
  { id: 'fieldService', targets: { allowLocation: false, allowAdHoc: true, allowCalendarEvent: true, allowTask: true } },
  { id: 'claims', targets: { allowLocation: true, allowAdHoc: true, allowCalendarEvent: false, allowTask: false } },
];

const MIN_RADIUS = 10;
const MAX_RADIUS = 10000;

export const FieldOperationsSettingsPage: React.FC = () => {
  const { t } = useTranslation(['fieldOperations', 'nav', 'common']);
  const { settings, loading: contextLoading, refresh } = useFieldOperationsSettings();

  const [draft, setDraft] = useState<FieldOperationsSettings>(settings);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setDraft(settings);
  }, [settings]);

  const patch = useCallback((changes: Partial<FieldOperationsSettings>) => {
    setSavedAt(null);
    setDraft((current) => ({ ...current, ...changes }));
  }, []);

  const hasTarget = draft.allowLocation || draft.allowAdHoc || draft.allowCalendarEvent || draft.allowTask;

  const save = async () => {
    if (draft.isEnabled && !hasTarget) {
      setError(t('settingsNeedsTarget'));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await updateFieldOperationsSettings({
        isEnabled: draft.isEnabled,
        allowLocation: draft.allowLocation,
        allowAdHoc: draft.allowAdHoc,
        allowCalendarEvent: draft.allowCalendarEvent,
        allowTask: draft.allowTask,
        mobileGpsRequired: draft.mobileGpsRequired,
        webGpsRequired: draft.webGpsRequired,
        enforceGeofenceOnCheckIn: draft.enforceGeofenceOnCheckIn,
        defaultRadiusMeters: draft.defaultRadiusMeters,
      });
      await refresh();
      setSavedAt(Date.now());
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('settingsFailedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const renderToggle = (
    key: keyof FieldOperationsSettings,
    labelKey: string,
    hintKey: string,
    disabled = false,
  ) => (
    <label className={styles.toggleRow}>
      <input
        type="checkbox"
        checked={Boolean(draft[key])}
        disabled={disabled || saving}
        onChange={(event) => patch({ [key]: event.target.checked } as Partial<FieldOperationsSettings>)}
      />
      <span className={styles.toggleText}>
        <span className={styles.toggleLabel}>{t(labelKey)}</span>
        <span className={styles.toggleHint}>{t(hintKey)}</span>
      </span>
    </label>
  );

  return (
    <div className={styles.page}>
      <Breadcrumb
        items={[
          { label: t('fieldOperations', { ns: 'nav' }), path: '/field-operations' },
          { label: t('settingsTitle'), path: '/field-operations/settings' },
        ]}
      />
      <div>
        <h2>{t('settingsTitle')}</h2>
        <p className={styles.pageIntro}>{t('settingsSubtitle')}</p>
      </div>

      {error ? <div className={styles.error}>{error}</div> : null}
      {contextLoading ? <Spinner text={t('loading')} /> : null}

      <section className={styles.sectionCard}>
        <h3 className={styles.sectionTitle}>{t('settingsModule')}</h3>
        <p className={styles.sectionHint}>{t('settingsModuleHint')}</p>
        <div className={styles.toggleList}>
          {renderToggle('isEnabled', 'settingsEnabled', 'settingsEnabledHint')}
        </div>
      </section>

      <section className={styles.sectionCard}>
        <h3 className={styles.sectionTitle}>{t('settingsPresets')}</h3>
        <p className={styles.sectionHint}>{t('settingsPresetsHint')}</p>
        <div className={styles.presets}>
          {PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={styles.presetCard}
              disabled={saving}
              onClick={() => patch(preset.targets)}
            >
              <span className={styles.presetName}>{t(`preset_${preset.id}`)}</span>
              <span className={styles.presetHint}>{t(`preset_${preset.id}_hint`)}</span>
            </button>
          ))}
        </div>
      </section>

      <section className={styles.sectionCard}>
        <h3 className={styles.sectionTitle}>{t('settingsTargets')}</h3>
        <p className={styles.sectionHint}>{t('settingsTargetsHint')}</p>
        <div className={styles.toggleList}>
          {renderToggle('allowLocation', 'target_location', 'target_location_hint')}
          {renderToggle('allowAdHoc', 'target_adHoc', 'target_adHoc_hint')}
          {renderToggle('allowCalendarEvent', 'target_calendarEvent', 'target_calendarEvent_hint')}
          {renderToggle('allowTask', 'target_task', 'target_task_hint')}
        </div>
      </section>

      <section className={styles.sectionCard}>
        <h3 className={styles.sectionTitle}>{t('settingsLocationRules')}</h3>
        <p className={styles.sectionHint}>{t('settingsLocationRulesHint')}</p>
        <div className={styles.toggleList}>
          {renderToggle('mobileGpsRequired', 'settingsMobileGps', 'settingsMobileGpsHint')}
          {renderToggle('webGpsRequired', 'settingsWebGps', 'settingsWebGpsHint')}
          {renderToggle('enforceGeofenceOnCheckIn', 'settingsGeofence', 'settingsGeofenceHint')}
          <label className={styles.numberField}>
            {t('settingsDefaultRadius')}
            <input
              type="number"
              className="text-input"
              min={MIN_RADIUS}
              max={MAX_RADIUS}
              value={draft.defaultRadiusMeters}
              disabled={saving}
              onChange={(event) => patch({ defaultRadiusMeters: Number(event.target.value) })}
            />
          </label>
        </div>
      </section>

      <div className={styles.footer}>
        <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={saving}>
          {saving ? t('saving') : t('settingsSave')}
        </button>
        {savedAt ? <span className={styles.saved}>{t('settingsSaved')}</span> : null}
      </div>
    </div>
  );
};
